import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { formatMoney } from 'features/company/companyUtils';
import { Card, EmptyState, ErrorState, HeroBand, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { localizedText } from 'shared/utilities/format';

export function CompanyPlansPage() {
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.companyPlans(1),
    queryFn: () => companyApi.plans({ page: 1, per_page: 20 }),
  });

  if (query.isLoading) {
    return <Skeleton rows={6} variant="cards" />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = query.data?.items || [];
  return (
    <div className="company-plans-page">
      <PageMeta titleKey="meta.companyPlans" />
      <HeroBand
        kicker={t('company.plans.kicker')}
        title={t('company.plans.title')}
        body={t('company.plans.body')}
      />
      {items.length === 0 ? (
        <EmptyState title={t('company.plans.empty')} />
      ) : (
        <div className="plan-grid">
          {items.map((plan) => (
            <Card key={plan.id} className="plan-card" interactive as="article">
              <div className="plan-card-head">
                <h2>{localizedText(plan, locale, ['name'])}</h2>
                {plan.is_featured ? <StatusChip tone="accent">{t('company.plans.featured')}</StatusChip> : null}
              </div>
              <p className="muted">{localizedText(plan, locale, ['description'])}</p>
              <ul className="plan-meta">
                <li>{t('company.plans.credits', { count: plan.attempt_credits })}</li>
                <li>{formatMoney(plan.price_minor, plan.currency, locale)}</li>
                {plan.validity_days ? <li>{t('company.plans.validity', { days: plan.validity_days })}</li> : null}
              </ul>
              <Link className="btn btn-primary" to={paths.companyPlan(plan.id)}>
                {t('company.plans.view')}
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
